import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantSettingsQuery, useUpdateTenantSettingMutation } from '@/entities/tenant'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'
import { TenantSettingKey } from '@/shared/constants/tenant-settings'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'

// Backend defaults to 10 rows per page; the duration keys sit beyond the first page.

interface TenantSettingsManagementProps {
  tenantId: string
  onDirtyChange?: (isDirty: boolean) => void
}

export function TenantSettingsManagement({
  tenantId,
  onDirtyChange,
}: TenantSettingsManagementProps) {
  const { t } = useTranslation()
  const { data: response, isLoading } = useTenantSettingsQuery(tenantId, {
    pageSize: DEFAULT_FETCH_LIMIT,
  })
  const updateMutation = useUpdateTenantSettingMutation()

  // Track only manually changed values to avoid useEffect sync loops
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({})

  const settings = response?.data?.data ?? []

  const durationSettings: Array<{ key: TenantSettingKey; labelKey: string }> = [
    {
      key: TenantSettingKey.TRIAL_DURATION_SECONDS,
      labelKey: 'tenant.settings.trialDurationMinutes',
    },
    {
      key: TenantSettingKey.USAGE_DURATION_SECONDS,
      labelKey: 'tenant.settings.usageDurationMinutes',
    },
    {
      key: TenantSettingKey.EXTEND_DURATION_SECONDS,
      labelKey: 'tenant.settings.extendDurationMinutes',
    },
    {
      key: TenantSettingKey.SURVEY_DURATION_SECONDS,
      labelKey: 'tenant.settings.surveyDurationMinutes',
    },
  ]
  const durationSettingKeys = new Set(durationSettings.map((item) => item.key))

  const toMinutes = (secondsRaw: string) => {
    if (!secondsRaw.trim()) {
      return ''
    }
    const seconds = Number(secondsRaw)
    if (!Number.isFinite(seconds)) {
      return ''
    }
    return String(seconds / 60)
  }

  const toSeconds = (minutesRaw: string) => {
    if (!minutesRaw.trim()) {
      return '0'
    }
    const minutes = Number(minutesRaw)
    if (!Number.isFinite(minutes) || minutes < 0) {
      return null
    }
    return String(Math.round(minutes * 60))
  }

  const getValue = (key: TenantSettingKey) => {
    const pendingValue = pendingChanges[key]
    if (pendingValue !== undefined) {
      return pendingValue
    }

    const currentValue = settings.find((s) => s.settingKey === key)?.settingValue ?? ''

    if (durationSettingKeys.has(key)) {
      return toMinutes(currentValue)
    }

    return currentValue
  }

  const handleChange = (key: TenantSettingKey, value: string) => {
    setPendingChanges((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async (key: TenantSettingKey) => {
    const value = pendingChanges[key]
    if (value === undefined) {
      return // No change to save
    }

    try {
      const valueToSave = durationSettingKeys.has(key) ? toSeconds(value) : value
      if (valueToSave === null) {
        showToast.error(t('tenant.settings.invalidMinutes'))
        return
      }

      await updateMutation.mutateAsync({ tenantId, key, value: valueToSave })
      showToast.success(t('tenant.settings.updateSuccess', { defaultValue: 'Cập nhật thành công' }))
      // Remove from pending changes after successful save
      setPendingChanges((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    } catch (error) {
      showToast.error(
        getApiErrorText(error) ??
          t('tenant.settings.updateError', { defaultValue: 'Cập nhật thất bại' }),
      )
    }
  }

  const handleSaveAll = async () => {
    const keysToSave = Object.keys(pendingChanges) as TenantSettingKey[]
    if (keysToSave.length === 0) return

    try {
      const valuesToSave = keysToSave.map((key) => {
        const rawValue = pendingChanges[key]
        if (rawValue === undefined) {
          return null
        }
        if (!durationSettingKeys.has(key)) {
          return { key, value: rawValue }
        }

        const converted = toSeconds(rawValue)
        if (converted === null) {
          return null
        }

        return { key, value: converted }
      })

      if (valuesToSave.includes(null)) {
        showToast.error(t('tenant.settings.invalidMinutes'))
        return
      }

      const normalizedValues = valuesToSave.filter((item) => item !== null)
      await Promise.all(
        normalizedValues.map(async (item) =>
          updateMutation.mutateAsync({ tenantId, key: item.key, value: item.value }),
        ),
      )
      showToast.success(t('tenant.settings.updateSuccess', { defaultValue: 'Cập nhật thành công' }))
      setPendingChanges({})
    } catch (error) {
      showToast.error(
        getApiErrorText(error) ??
          t('tenant.settings.updateError', { defaultValue: 'Cập nhật thất bại' }),
      )
    }
  }

  const hasChanges = Object.keys(pendingChanges).length > 0

  useEffect(() => {
    onDirtyChange?.(hasChanges)
  }, [hasChanges, onDirtyChange])

  if (isLoading) {
    return (
      <div className="border border-zinc-200 rounded-xl p-6 space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200">
        <h3 className="text-base font-semibold text-zinc-900">{t('tenant.settings.title')}</h3>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {durationSettings.map((item) => (
              <div key={item.key} className="flex flex-col gap-2">
                <Label className="text-zinc-700">{t(item.labelKey)}</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={getValue(item.key)}
                    onChange={(e) => handleChange(item.key, e.target.value)}
                    placeholder={t('tenant.settings.minutesPlaceholder')}
                    className="flex-1"
                  />
                  {pendingChanges[item.key] !== undefined && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => handleSave(item.key)}
                      disabled={updateMutation.isPending}
                    >
                      {t('common.save')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end border-t border-zinc-100 px-6 py-4">
        <Button onClick={handleSaveAll} disabled={!hasChanges || updateMutation.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
